/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * HyusnSu Sim (hssim@rsupport.com)
 *
 * @description
 *
 * @created at Thu Mar 23 2023
 **/

import React, { forwardRef, RefObject } from "react";
import { useTheme } from "@emotion/react";
import cx from "classnames";

import { Typography } from "@/frameworks/designStuff/components/Typography";
import { Button } from "@/frameworks/designStuff/components";
import { ButtonBase } from "@/frameworks/designStuff/components";
import { Maybe } from "@/frameworks/libraries/components";

import useKeyEvent from "./useMenuEvent";
import {
	buttonStyle,
	colorItemStyle,
	colorStyle,
	cursorStyle,
	descriptionStyle,
	headerStyle,
	linkStyle,
	listStyle,
	menuGroupStyle,
	menuItemStyle,
	menuStyle,
	selectedStyle,
	subTitleStyle,
	textEllipsis
} from "./private/emotion";

export interface MenuCompProps extends React.HTMLAttributes<HTMLDivElement> {
	isGroup?: boolean;
	fullWidth?: boolean;
	maxWidth?: UnitString;
}

export const MenuComp = forwardRef(
	(
		{ className, isGroup = false, fullWidth = false, maxWidth = "320px", children, ...rest }: MenuCompProps,
		ref: React.ForwardedRef<HTMLDivElement>
	) => {
		return (
			<div ref={ref} css={[menuStyle(isGroup, fullWidth, maxWidth)]} className={className} {...rest}>
				{children}
			</div>
		);
	}
);

export interface ListProps extends React.HTMLAttributes<HTMLUListElement> {
	maxHeight?: UnitString | "initial";
}

const List = forwardRef(
	(
		{ id, className, maxHeight = "initial", children, ...rest }: ListProps,
		ref: React.ForwardedRef<HTMLUListElement>
	) => {
		const { handleKeyDown, handleFocus, handleBlur } = useKeyEvent(ref as RefObject<HTMLUListElement>);

		return (
			<ul
				ref={ref}
				id={id}
				css={listStyle(maxHeight)}
				className={className}
				onKeyDown={handleKeyDown}
				onFocus={handleFocus}
				onBlur={handleBlur}
				{...rest}
			>
				{children}
			</ul>
		);
	}
);

export interface ItemProps extends React.HTMLAttributes<HTMLLIElement> {
	align?: Align;
	text?: string;
	typoColor?: keyof Colors;
	typoSize?: DesignStuff.Typography.TypoSizeType;
	startEnhancer?: React.ReactNode;
	endEnhancer?: React.ReactNode;
	description?: React.ReactNode;
	focus?: boolean;
	disabled?: boolean;
}

const Item = forwardRef(
	(
		{
			id,
			className,
			align = "flex-start",
			onClick,
			typoSize,
			typoColor,
			startEnhancer = null,
			endEnhancer = null,
			description = null,
			focus = false,
			disabled = false,
			children,
			...rest
		}: ItemProps,
		ref: React.ForwardedRef<HTMLLIElement>
	) => {
		const theme = useTheme();

		return (
			<li ref={ref} id={id} className={className} onClick={onClick} {...rest}>
				<Button
					variant="contained"
					size="small"
					typoColor={typoColor}
					typoSize={typoSize}
					css={[cursorStyle, buttonStyle, menuItemStyle(align)]}
					data-focus={focus}
					startEnhancer={startEnhancer}
					endEnhancer={endEnhancer}
					disabled={disabled}
					filled={focus ? "secondary" : "transparent"}
				>
					{children}
				</Button>
				<Maybe test={Boolean(description)}>
					<div css={descriptionStyle(theme)}>{description}</div>
				</Maybe>
			</li>
		);
	}
);

const MentionItem = forwardRef(
	({ id, className, onClick, children, ...rest }: ItemProps, ref: React.ForwardedRef<HTMLLIElement>) => {
		return (
			<li ref={ref} id={id} className={className} onClick={onClick} {...rest}>
				<div css={[cursorStyle]}>{children}</div>
			</li>
		);
	}
);

const ColorItem = forwardRef(
	(
		{
			id,
			className,
			color,
			width = "28px",
			radius = "6px",
			align = "center",
			onClick,
			selected,
			disabled,
			isAvatar = false,
			children,
			...rest
		}: ItemProps & {
			color: DesignStuff.WorkGroup.ColorList;
			selected: boolean;
			disabled: boolean;
			width?: UnitString;
			radius?: UnitString;
			isAvatar?: boolean;
		},
		ref: React.ForwardedRef<HTMLLIElement>
	) => {
		const theme = useTheme();
		return (
			<li
				ref={ref}
				css={selectedStyle(theme)}
				id={id}
				className={className}
				onClick={onClick}
				{...rest}
				style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
			>
				<ButtonBase
					disabled={disabled}
					css={[
						cursorStyle,
						buttonStyle,
						menuItemStyle(align),
						colorStyle(
							theme,
							isAvatar
								? theme.colors[`${color}_10_100`]
								: (theme.colors[`${color}_${disabled ? "10" : "50"}_100`] as string)
						),
						colorItemStyle(
							theme,
							selected,
							theme.colors[`${color}_${disabled ? "10" : "50"}_100`],
							disabled,
							width,
							radius
						)
					]}
					data-focus="false"
				>
					{children}
				</ButtonBase>
			</li>
		);
	}
);

export interface LinkItemProps extends React.HTMLAttributes<HTMLLIElement> {
	className?: string;
	align?: Align;
	text?: string;
	href: string;
	target: "_blank" | "_self" | "_parent" | "_top";
	children: React.ReactNode;
}

const LinkItem = forwardRef(
	(
		{ id, className, align = "flex-start", href = "/", target = "_blank", children, ...rest }: LinkItemProps,
		ref: React.ForwardedRef<HTMLLIElement>
	) => {
		return (
			<li ref={ref} css={cursorStyle} className={className} {...rest}>
				<Button variant="text" size="small" css={[cursorStyle, buttonStyle]} data-focus="false">
					<Typography.a
						typoSize="Body3_MD"
						css={[linkStyle, menuItemStyle(align), textEllipsis]}
						id={id}
						href={href}
						target={target}
						data-focus="false"
					>
						{children}
					</Typography.a>
				</Button>
			</li>
		);
	}
);

const Header = forwardRef(
	(
		{ id, className, children, ...rest }: React.HTMLAttributes<HTMLDivElement>,
		ref: React.ForwardedRef<HTMLDivElement>
	) => {
		return (
			<div ref={ref} css={[headerStyle, textEllipsis]} id={id} className={className} {...rest}>
				{children}
			</div>
		);
	}
);

function Title({ id, className, children, ...rest }: React.HTMLAttributes<HTMLDivElement>) {
	return (
		<Typography.p typoSize="Title1" id={id} className={cx(className, "menu-title")} {...rest}>
			{children}
		</Typography.p>
	);
}

function SubTitle({ id, className, children, ...rest }: React.HTMLAttributes<HTMLParagraphElement>) {
	return (
		<Typography.p
			typoSize="Body4"
			css={[subTitleStyle, textEllipsis]}
			id={id}
			className={cx(className, "subtitle")}
			{...rest}
		>
			{children}
		</Typography.p>
	);
}

const Group = forwardRef(
	({ children, ...rest }: React.HTMLAttributes<HTMLDivElement>, ref: React.ForwardedRef<HTMLDivElement>) => {
		return (
			<div ref={ref} css={[menuGroupStyle]} {...rest}>
				{children}
			</div>
		);
	}
);

export interface IMenuProps
	extends React.ForwardRefExoticComponent<MenuCompProps & React.RefAttributes<HTMLDivElement>> {
	Group: typeof Group;
	List: typeof List;
	Item: typeof Item;
	MentionItem: typeof MentionItem;
	LinkItem: typeof LinkItem;
	Header: typeof Header;
	Title: typeof Title;
	SubTitle: typeof SubTitle;
	Color: typeof ColorItem;
}

const Menu = {
	...MenuComp,
	Group,
	List,
	Item,
	LinkItem,
	MentionItem,
	Header,
	Title,
	SubTitle,
	Color: ColorItem
} as IMenuProps;

export default Menu;
